import { useCallback, useState, useEffect, MutableRefObject } from 'react';

import { exitFullScreen, enterFullScreen, isFullscreen } from '@gem/utils';

interface IVideoPlayerFullscreenMode {
  toggleFullScreenMode: () => void;
  isFullScreenMode: boolean;
}

interface IVideoPlayerFullscreenModeParams {
  videoContainerRef: MutableRefObject<HTMLVideoElement | null>;
  newIsFullScreenModeState: boolean;
  onToggleFullScreenMode: (value: boolean) => void;
}

export function useVideoPlayerFullscreenMode({
  videoContainerRef,
  newIsFullScreenModeState,
  onToggleFullScreenMode,
}: IVideoPlayerFullscreenModeParams): IVideoPlayerFullscreenMode {
  const [isFullScreenMode, setFullScreenMode] = useState(false);

  const toggleFullScreenMode = useCallback(() => {
    isFullscreen() ? exitFullscreenMode() : enterFullscreenMode();
  }, []);

  const enterFullscreenMode = useCallback(async () => {
    await enterFullScreen(videoContainerRef.current || undefined);
    updateFullscreenMode();
  }, []);

  const exitFullscreenMode = useCallback(async () => {
    await exitFullScreen();
    updateFullscreenMode();
  }, []);

  const updateFullscreenMode = useCallback(() => {
    setFullScreenMode(isFullscreen());
    onToggleFullScreenMode(isFullscreen());
  }, []);

  useEffect(() => {
    if (newIsFullScreenModeState) {
      enterFullscreenMode();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('fullscreenchange', updateFullscreenMode);

    return () => {
      document.addEventListener('fullscreenchange', updateFullscreenMode);
    };
  }, []);

  return { isFullScreenMode, toggleFullScreenMode };
}
